import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Trash2, Download, AlertTriangle, CheckCircle, Mail, Settings, Shield, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';

const DataDeletionPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <Trash2 className="w-8 h-8 text-red-600 mr-3" />
            <h1 className="text-3xl font-bold" style={{ color: '#000000' }}>User Data Deletion Instructions</h1>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <p className="font-semibold" style={{ color: '#000000' }}>
                  Important: Data deletion is permanent and cannot be undone.
                </p>
              </div>
            </div>

            <p className="mb-6" style={{ color: '#000000' }}>
              This document provides instructions for users who want to delete their data from Quixocial, including data obtained from Facebook and Instagram. This is required for Meta app review compliance.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>How to Delete Your Data</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center mb-4">
                  <Settings className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold" style={{ color: '#000000' }}>Method 1: Privacy Dashboard</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: '#333333' }}>Recommended method for logged-in users</p>
                <div className="space-y-2 text-sm" style={{ color: '#000000' }}>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                    <span>Log into your account</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                    <span>Go to Privacy & Compliance</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                    <span>Select "Delete Account"</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex items-center mb-4">
                  <Mail className="w-8 h-8 text-green-600 mr-3" />
                  <h3 className="text-lg font-semibold" style={{ color: '#000000' }}>Method 2: GDPR Request</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: '#333333' }}>Submit a formal data erasure request</p>
                <div className="space-y-2 text-sm" style={{ color: '#000000' }}>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span>Visit GDPR Request Form</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span>Select "Data Erasure Request"</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span>Verify your identity</span>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <div className="flex items-center mb-4">
                  <Settings className="w-8 h-8 text-purple-600 mr-3" />
                  <h3 className="text-lg font-semibold" style={{ color: '#000000' }}>Method 3: Social Media</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: '#333333' }}>Remove app through connected platforms</p>
                <div className="space-y-2 text-sm" style={{ color: '#000000' }}>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-purple-600 mr-2" />
                    <span>Go to Facebook/Instagram Settings</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-purple-600 mr-2" />
                    <span>Find "Apps and Websites"</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-purple-600 mr-2" />
                    <span>Remove Quixocial</span>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Data Deletion Categories</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold mb-2 flex items-center" style={{ color: '#000000' }}>
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    Deleted Immediately
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Personal data (email, name, phone, profile picture)</li>
                    <li>User content (posts, media files, drafts)</li>
                    <li>AI-generated content and tasks</li>
                    <li>Social media access tokens</li>
                    <li>Brand kit assets and client data</li>
                    <li>Team collaborations and preferences</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold mb-2 flex items-center" style={{ color: '#000000' }}>
                    <AlertTriangle className="w-5 h-5 text-blue-600 mr-2" />
                    Anonymized & Retained
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Usage analytics (indefinitely - legitimate interest)</li>
                    <li>Performance metrics (anonymized)</li>
                    <li>Feature usage statistics (no personal identifiers)</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold mb-2 flex items-center" style={{ color: '#000000' }}>
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                    Retained for Legal Compliance
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Financial records (7 years - tax law requirement)</li>
                    <li>Security logs (2 years - fraud prevention)</li>
                    <li>Audit trails (compliance requirement)</li>
                  </ul>
                  <p className="text-xs mt-2 text-yellow-700">
                    <strong>Legal Basis:</strong> Legal obligation and legitimate interest under GDPR Article 6
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h3 className="font-semibold mb-2 flex items-center" style={{ color: '#000000' }}>
                    <Shield className="w-5 h-5 text-purple-600 mr-2" />
                    GDPR Article 17 Compliance
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Right to erasure fully implemented</li>
                    <li>Legal basis provided for any retention</li>
                    <li>Audit trail maintained for compliance</li>
                    <li>Transparency about data handling</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Data Deletion Timeline</h2>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: '#000000' }}>
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    Immediate (Within 24 hours)
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Account access disabled</li>
                    <li>Personal data deleted</li>
                    <li>User content removed</li>
                    <li>Social media connections revoked</li>
                    <li>Team access removed</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: '#000000' }}>
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                    Retained (Legal Basis)
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Financial records: 7 years</li>
                    <li>Security logs: 2 years</li>
                    <li>Anonymized analytics: Indefinite</li>
                    <li>Audit trails: As required</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: '#000000' }}>
                    <Shield className="w-5 h-5 text-blue-600 mr-2" />
                    Compliance Measures
                  </h3>
                  <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>All deletions logged</li>
                    <li>GDPR Article 17 compliance</li>
                    <li>Legal basis documented</li>
                    <li>Audit trail maintained</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Before You Delete - Important Considerations</h2>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6">
              <div className="flex items-start">
                <Download className="w-6 h-6 text-yellow-600 mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#000000' }}>Download Your Data First</h3>
                  <p className="mb-3" style={{ color: '#000000' }}>Consider downloading your data before deletion:</p>
                  <ol className="list-decimal list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                    <li>Go to Privacy & Compliance → My Data</li>
                    <li>Click "Export My Data"</li>
                    <li>Submit data portability request</li>
                    <li>Receive download link via email</li>
                    <li>Download expires after 7 days</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Alternative Options</h3>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Account deactivation (temporary)</li>
                  <li>Data export for backup</li>
                  <li>Selective deletion of content</li>
                  <li>Account transfer to team members</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Impact on Team and Clients</h3>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Inform team members before deletion</li>
                  <li>Transfer client ownership if needed</li>
                  <li>Download important client assets</li>
                  <li>Notify clients of service interruption</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Contact Information</h2>
            
            <div className="bg-gray-100 p-6 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Data Deletion Support</h3>
                  <div className="space-y-1 text-sm" style={{ color: '#000000' }}>
                    <p><strong>Email:</strong> info@quixocial.com</p>
                    <p><strong>Subject:</strong> "Data Deletion Request"</p>
                    <p><strong>Response Time:</strong> 5 business days</p>
                  </div>
                </div>

              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>Your Rights</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Under GDPR (EU Users)</h3>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Right to erasure ("right to be forgotten")</li>
                  <li>Right to data portability</li>
                  <li>Right to restrict processing</li>
                  <li>Right to object to processing</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold mb-2" style={{ color: '#000000' }}>Under CCPA (California Users)</h3>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Right to delete personal information</li>
                  <li>Right to know what data is collected</li>
                  <li>Right to opt-out of data sharing</li>
                  <li>Right to non-discrimination</li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#000000' }}>Take Action</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/app/gdpr-compliance" className="block">
                  <div className="bg-white p-4 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-2">
                      <Shield className="w-5 h-5 text-blue-600 mr-2" />
                      <h3 className="font-semibold" style={{ color: '#000000' }}>Privacy Dashboard</h3>
                    </div>
                    <p className="text-sm" style={{ color: '#666666' }}>
                      Access your privacy controls and delete your account (requires login)
                    </p>
                  </div>
                </Link>
                
                <Link to="/gdpr-request" className="block">
                  <div className="bg-white p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-2">
                      <FileText className="w-5 h-5 text-green-600 mr-2" />
                      <h3 className="font-semibold" style={{ color: '#000000' }}>GDPR Request Form</h3>
                    </div>
                    <p className="text-sm" style={{ color: '#666666' }}>
                      Submit a formal data erasure request (no login required)
                    </p>
                  </div>
                </Link>
              </div>
            </div>

            <div className="bg-slate-900 text-white p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-2" />
                Legal Compliance & Data Protection
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">GDPR Article 17 - Right to Erasure</h4>
                  <ul className="text-sm space-y-1 text-gray-300">
                    <li>• Personal data deleted immediately upon request</li>
                    <li>• Legal basis provided for any data retention</li>
                    <li>• Transparent about data handling practices</li>
                    <li>• Audit trail maintained for compliance</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Data Retention Legal Basis</h4>
                  <ul className="text-sm space-y-1 text-gray-300">
                    <li>• Financial records: Legal obligation (tax laws)</li>
                    <li>• Security logs: Legitimate interest (fraud prevention)</li>
                    <li>• Analytics: Legitimate interest (anonymized)</li>
                    <li>• All deletions logged for audit compliance</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gradient-to-r from-red-100 to-orange-100 rounded-lg">
              <p className="text-sm text-center" style={{ color: '#000000' }}>
                This data deletion process is designed to give you complete control over your personal information while complying with GDPR, CCPA, and Meta's platform requirements.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionPage;